import React, { useState } from "react";
import { Check, Zap, Archive } from "lucide-react";
import { FiPackage } from 'react-icons/fi';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { getReleaseStatusColor } from "@/lib/colorsMap";

export function TaskScopeSelect({ value, releases = [], selectedReleaseId, onChange, className }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Find the selected release if there is one
  const selectedRelease = releases.find(r => r._id === selectedReleaseId || r.id === selectedReleaseId);

  // Separate releases into active and inactive (backlog)
  const activeReleases = [];
  const backlogReleases = [];
  
  releases.forEach(release => {
    // A release is active if it has a start date in the past or today
    const isActive = release.startDate && new Date(release.startDate) <= new Date();
    if (isActive) {
      activeReleases.push(release);
    } else {
      backlogReleases.push(release);
    }
  });

  // Manual filtering to maintain order and hierarchy
  const searchLower = search.toLowerCase();
  
  // Filter releases
  const filteredActiveReleases = activeReleases.filter(r => 
    !search || r.name.toLowerCase().includes(searchLower)
  );
  const filteredBacklogReleases = backlogReleases.filter(r => 
    !search || r.name.toLowerCase().includes(searchLower)
  );

  // Active Week/Backlog show if they match search OR if they have matching releases
  const activeWeekMatches = !search || "active week".includes(searchLower) || "active".includes(searchLower);
  const backlogMatches = !search || "backlog".includes(searchLower);
  
  // Always show sections to maintain hierarchy context, but dim them if they don't match
  const showActiveWeekSection = activeWeekMatches || filteredActiveReleases.length > 0;
  const showBacklogSection = backlogMatches || filteredBacklogReleases.length > 0;

  const hasResults = showActiveWeekSection || showBacklogSection;

  const handleScopeChange = (newScope, releaseId = null) => {
    onChange({ scope: newScope, releaseId });
    setSearch("");
    setOpen(false);
  };

  // Render the trigger button
  const renderTrigger = () => {
    return (
      <Button 
        variant="ghost" 
        size="sm" 
        type="button"
        className="h-7 px-1.5 -ml-1.5 flex items-center gap-2"
      >
        {selectedRelease ? (
          <>
            <FiPackage className={cn("w-[18px] h-[18px]", getReleaseStatusColor(selectedRelease.status || 'upcoming').icon)} />
            {selectedRelease.name}
          </>
        ) : value === 'tracked' ? (
          <>
            <Zap className="w-[18px] h-[18px] text-emerald-600" />
            Active Week
          </>
        ) : (
          <>
            <Archive className="w-[18px] h-[18px] text-neutral-600" />
            Backlog
          </>
        )}
      </Button>
    );
  };

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setSearch("");
        }
      }}>
        <PopoverTrigger asChild>
          {renderTrigger()}
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" side="top" align="start">
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder="Set scope..." 
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {!hasResults && <CommandEmpty>No results found.</CommandEmpty>}
              <CommandGroup>
                {/* Active Week Section */}
                {showActiveWeekSection && (
                  <>
                    {/* Active Week (No Release) - always shown if section is visible */}
                    <CommandItem
                      value="active-week"
                      onSelect={() => handleScopeChange('tracked', null)}
                      className={cn(!activeWeekMatches && "opacity-50")}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <Zap className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span>Active Week</span>
                        <span className="text-xs text-muted-foreground">(No Release)</span>
                      </div>
                      {value === 'tracked' && !selectedReleaseId && (
                        <Check className="ml-auto h-4 w-4 flex-shrink-0" />
                      )}
                    </CommandItem>

                    {/* Active Releases (nested under Active Week) */}
                    {filteredActiveReleases.length > 0 && (
                      <div className="relative ml-4 border-l-2 border-emerald-200 dark:border-emerald-800">
                        {filteredActiveReleases.map((release) => {
                          const colors = getReleaseStatusColor(release.status || 'upcoming');
                          const releaseId = release._id || release.id;
                          return (
                            <CommandItem
                              key={releaseId}
                              value={`active-release-${releaseId}`}
                              onSelect={() => handleScopeChange('tracked', releaseId)}
                              className="pl-4 rounded-none"
                            >
                              <div className="flex items-center gap-2 w-full text-sm">
                                <FiPackage className={cn("w-3.5 h-3.5 flex-shrink-0", colors.icon)} />
                                <span className="truncate flex-1">{release.name}</span>
                                {release.targetDate && (
                                  <span className="text-xs text-muted-foreground flex-shrink-0">
                                    {format(new Date(release.targetDate), 'MMM d')}
                                  </span>
                                )}
                              </div>
                              {selectedReleaseId === releaseId && (
                                <Check className="ml-2 h-4 w-4 flex-shrink-0" />
                              )}
                            </CommandItem>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {/* Backlog Section */}
                {showBacklogSection && (
                  <>
                    {/* Backlog (No Release) - always shown if section is visible */}
                    <CommandItem
                      value="backlog"
                      onSelect={() => handleScopeChange('backlog', null)}
                      className={cn("mt-2", !backlogMatches && "opacity-50")}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <Archive className="w-4 h-4 text-neutral-600 flex-shrink-0" />
                        <span>Backlog</span>
                        <span className="text-xs text-muted-foreground">(No Release)</span>
                      </div>
                      {value === 'backlog' && !selectedReleaseId && (
                        <Check className="ml-auto h-4 w-4 flex-shrink-0" />
                      )}
                    </CommandItem>

                    {/* Backlog Releases (nested under Backlog) */}
                    {filteredBacklogReleases.length > 0 && (
                      <div className="relative ml-4 border-l-2 border-neutral-200 dark:border-neutral-800">
                        {filteredBacklogReleases.map((release) => {
                          const colors = getReleaseStatusColor(release.status || 'upcoming');
                          const releaseId = release._id || release.id;
                          return (
                            <CommandItem
                              key={releaseId}
                              value={`backlog-release-${releaseId}`}
                              onSelect={() => handleScopeChange('backlog', releaseId)}
                              className="pl-4 rounded-none"
                            >
                              <div className="flex items-center gap-2 w-full text-sm">
                                <FiPackage className={cn("w-3.5 h-3.5 flex-shrink-0", colors.icon)} />
                                <span className="truncate flex-1">{release.name}</span>
                                {release.targetDate && (
                                  <span className="text-xs text-muted-foreground flex-shrink-0">
                                    {format(new Date(release.targetDate), 'MMM d')}
                                  </span>
                                )}
                              </div>
                              {selectedReleaseId === releaseId && (
                                <Check className="ml-2 h-4 w-4 flex-shrink-0" />
                              )}
                            </CommandItem>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
